import { create } from "zustand";
import { persist } from "zustand/middleware";

export type Theme = "light" | "dark" | "system";
/** Colour family, orthogonal to light/dark: the original console or the warm console v1.0. */
export type Palette = "platform" | "warm";

interface UiState {
  theme: Theme;
  setTheme: (t: Theme) => void;
  palette: Palette;
  setPalette: (p: Palette) => void;
  /** Last project opened per workspace slug, used by the workspace index redirect. */
  lastProject: Record<string, string>;
  rememberProject: (workspace: string, project: string) => void;
  /** Attention items the user dismissed or snoozed, keyed by id → expiry epoch ms. */
  snoozed: Record<string, number>;
  snooze: (id: string, ms: number) => void;
  unsnooze: (id: string) => void;
}

export const useUiStore = create<UiState>()(
  persist(
    (set) => ({
      theme: "system",
      setTheme: (theme) => set({ theme }),
      // Warm console is the product's look (decided 2026-09-13); Platform stays as a choice.
      palette: "warm",
      setPalette: (palette) => set({ palette }),
      lastProject: {},
      rememberProject: (workspace, project) =>
        set((s) => ({ lastProject: { ...s.lastProject, [workspace]: project } })),
      snoozed: {},
      snooze: (id, ms) => set((s) => ({ snoozed: { ...s.snoozed, [id]: Date.now() + ms } })),
      unsnooze: (id) =>
        set((s) => {
          const { [id]: _gone, ...rest } = s.snoozed;
          return { snoozed: rest };
        }),
    }),
    { name: "auradata.ui" },
  ),
);

const DARK_QUERY = "(prefers-color-scheme: dark)";

/**
 * Stamps the resolved theme on <html>. "System" used to remove the attribute, and since the
 * tokens only key off `data-theme="dark"`, System never went dark; it now resolves against the
 * OS setting and follows it live (see `watchSystemTheme`).
 */
export function applyTheme(theme: Theme) {
  const root = document.documentElement;
  const dark = theme === "dark" || (theme === "system" && window.matchMedia(DARK_QUERY).matches);
  root.setAttribute("data-theme", dark ? "dark" : "light");
}

/** Re-applies a System theme when the OS switches; returns the unsubscribe. */
export function watchSystemTheme(theme: Theme): () => void {
  if (theme !== "system") return () => {};
  const mq = window.matchMedia(DARK_QUERY);
  const onChange = () => applyTheme("system");
  mq.addEventListener("change", onChange);
  return () => mq.removeEventListener("change", onChange);
}

export function applyPalette(palette: Palette) {
  const root = document.documentElement;
  if (palette === "platform") root.removeAttribute("data-palette");
  else root.setAttribute("data-palette", palette);
}
